/**
 * Prerequisite Check — LDG-based prerequisite gating
 *
 * Checks whether a family's hard prerequisites are met
 * based on the Learning Dependency Graph (LDG).
 *
 * Extracted from adaptiveEngine.ts in O-2 (god-file split).
 */

import { getTierRangeForGroup } from '../../content';
import { getLDG } from '../../governance';
import { GroupId } from '../../config/systemConfig';
import { FamilyMasteryState, getFamilyMasteryKey } from '../familyMasteryEngine';
import { V11_CONFIG, debugLog } from './selectionConfig';

/**
 * Check if a family's hard prerequisites are met based on mastery state.
 * Returns true if all hard prerequisites have mastery >= threshold.
 *
 * RESOLVES: CONFLICT-01 (LDG Exists But Is Not Enforced)
 *
 * v11.4: Default threshold raised from STRUGGLING_THRESHOLD (40) to
 * LDG_PREREQUISITE_MASTERY (60) for tighter prerequisite enforcement.
 * Students must demonstrate reasonable understanding of prerequisites
 * before advancing to dependent topics.
 */
export function arePrerequisitesMet(familyId: string, familyMastery: Record<string, FamilyMasteryState>, selectedGroup: GroupId, threshold: number = V11_CONFIG.LDG_PREREQUISITE_MASTERY): boolean {
  const graph = getLDG();
  const node = graph.nodes.get(familyId);
  if (!node) return true; // Unknown node — allow (don't block)

  // No prerequisites — always allowed
  if (node.prerequisites.length === 0) return true;

  // v11.7: Get the tier range for the current group.
  // Prerequisites whose family tier falls BELOW the current group's minimum
  // are auto-passed. Rationale: if a student is placed in group 7, group 6
  // content is assumed mastered (or at least should not block progression).
  // Without this, group-scoped mastery keys (e.g., "GET-BREUK-VEREENVOUDIG_g7")
  // always return 0 for lower-group prerequisites, permanently locking
  // dependent families and forcing the emergency fallback that bypasses
  // the LDG entirely — defeating its purpose.
  const groupTierRange = getTierRangeForGroup(selectedGroup);

  // Check each hard prerequisite
  for (const prereqId of node.prerequisites) {
    const edge = graph.edges.find(e => e.from === prereqId && e.to === familyId);
    // Only enforce hard prerequisites (soft are recommendations)
    if (edge && edge.type === 'soft') continue;

    // v11.7: Auto-pass prerequisites from lower groups.
    // Check if the prerequisite family's tier is below the current group's range.
    const prereqNode = graph.nodes.get(prereqId);
    if (prereqNode && prereqNode.tier < groupTierRange.min) {
      debugLog(`   ✅ Prerequisite auto-passed (lower group): ${prereqId} (tier ${prereqNode.tier} < group ${selectedGroup} min ${groupTierRange.min})`);
      continue;
    }
    const masteryKey = getFamilyMasteryKey(prereqId, selectedGroup);
    const prereqState = familyMastery[masteryKey];

    // v11.5: Use STORED mastery (not decayed) for prerequisite gating.
    //
    // Rationale: Prerequisites test "did you ever learn this?" not
    // "do you remember it right now?" A student who demonstrated mastery
    // (score 80) should not get locked out of dependent content after
    // 80 days of inactivity due to decay dropping below the threshold.
    //
    // Decayed mastery is still used for PRIORITY SCORING (in
    // selectAdaptiveQuestionV11), which naturally surfaces decayed
    // prerequisites as high-priority review targets. This is the correct
    // behavior — the student sees the prerequisite as a review item,
    // rather than being locked out of dependent content.
    //
    // Design decision: Use 0 as fallback for missing prerequisites (lenient approach)
    // to allow students to progress rather than being locked out of dependent content.
    const effectiveMastery = prereqState?.masteryScore ?? 0;
    if (effectiveMastery < threshold) {
      debugLog(`   🔒 Prerequisite not met: ${prereqId} (stored mastery: ${effectiveMastery}, threshold: ${threshold}) blocks ${familyId}`);
      return false;
    }
  }
  return true;
}